import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { getUserProfile } from '../services/auth';
import { getFavorites } from '../services/favorites';
import { setUser, setLoading, setError, clearAuth } from '../store/slices/authSlice';
import { setFavorites } from '../store/slices/voicesSlice';
import { convertTimestampToDate } from '../utils/dateUtils';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { RootState } from '../store';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 5000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const retryOperation = async <T>(
  operation: () => Promise<T>,
  retries: number = MAX_RETRIES,
  delayMs: number = RETRY_DELAY
): Promise<T> => {
  try {
    return await operation();
  } catch (error: any) {
    if (retries > 0 && (
      error.code === 'auth/network-request-failed' ||
      error.code === 'unavailable' || 
      error.code === 'network-request-failed'
    )) {
      const nextDelay = Math.min(delayMs * 2, MAX_RETRY_DELAY);
      await delay(delayMs);
      return retryOperation(operation, retries - 1, nextDelay);
    }
    throw error;
  }
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const loading = useSelector((state: RootState) => state.auth.loading);
  const [initialized, setInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let unsubscribeAuth: () => void;
    let unsubscribeToken: () => void;

    const initializeAuth = async () => {
      try {
        dispatch(setLoading(true));
        
        // Listen for auth state changes with retry logic
        const setupAuthListener = async () => {
          return new Promise<void>((resolve, reject) => {
            try {
              unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
                try {
                  if (firebaseUser) {
                    // Get user profile with retry logic
                    const userProfile = await retryOperation(async () => {
                      const profile = await getUserProfile(firebaseUser.uid);
                      if (!profile) {
                        throw new Error('User profile not found');
                      }
                      return profile;
                    });

                    dispatch(setUser({
                      ...userProfile,
                      createdAt: convertTimestampToDate(userProfile.createdAt),
                      lastLoginAt: convertTimestampToDate(userProfile.lastLoginAt)
                    }));

                    // Load favorites with retry logic
                    const favorites = await retryOperation(() => getFavorites(firebaseUser.uid));
                    dispatch(setFavorites(favorites));
                  } else {
                    dispatch(clearAuth());
                    dispatch(setFavorites([]));
                  }
                  resolve();
                } catch (err: any) {
                  reject(err);
                }
              }, reject);
            } catch (err) {
              reject(err);
            }
          });
        };

        await retryOperation(setupAuthListener);

        // Listen for ID token changes with retry logic
        const setupTokenListener = async () => {
          return new Promise<void>((resolve, reject) => {
            try {
              unsubscribeToken = onIdTokenChanged(auth, async (firebaseUser) => {
                try {
                  if (firebaseUser) {
                    // Force token refresh to get latest email verification status
                    await retryOperation(() => firebaseUser.getIdToken(true));
                    
                    // Update Firestore if email is verified
                    if (firebaseUser.emailVerified) {
                      try {
                        const userRef = doc(db, 'users', firebaseUser.uid);
                        await retryOperation(() => 
                          updateDoc(userRef, {
                            emailVerified: true
                          })
                        );
                      } catch (error) {
                        console.error('Error updating email verification status:', error);
                      }
                    }
                  }
                  resolve();
                } catch (err) {
                  reject(err);
                }
              }, reject);
            } catch (err) {
              reject(err);
            }
          });
        };

        await retryOperation(setupTokenListener);

      } catch (err: any) {
        console.error('Auth initialization error:', err);
        
        // Only retry on network-related errors
        if ((err.code === 'auth/network-request-failed' || err.code === 'unavailable') && retryCount < MAX_RETRIES) {
          setRetryCount(prev => prev + 1);
          await delay(RETRY_DELAY * Math.pow(2, retryCount));
          return;
        }

        dispatch(setError(err.message || 'Failed to initialize authentication'));
        dispatch(clearAuth());
      } finally {
        setInitialized(true);
        dispatch(setLoading(false));
      }
    };

    initializeAuth();

    return () => {
      if (unsubscribeAuth) {
        unsubscribeAuth();
      }
      if (unsubscribeToken) {
        unsubscribeToken();
      }
    };
  }, [dispatch, retryCount]);

  return { initialized, user, loading };
};